import { AttributeValueInput, MedicationValue } from "../types";
import { MappingContext } from "../orchestrator";
import {
  medicationGridMapperConfigSchema,
  medicationGridResponseSchema,
} from "../schemas";
import { withScope, captureException } from "@sentry/react";
import { memoizeMapperFactory } from "../utils/memoize";

function createMedicationInterval(
  statusType: string
): MedicationValue["intervals"] {
  if (statusType === "current") {
    return [{ start: {}, end: { current: true } }];
  }
  // Handle all past statuses
  if (statusType.startsWith("stopped_")) {
    // When isBeforeX=true, the API requires only year to be set
    // Default to 2024 as most questions are about stopping long ago
    return [{ start: {}, end: { isBeforeX: true, year: 2024 } }];
  }
  return undefined;
}

/**
 * Internal implementation of medicationGridMapper without memoization.
 */
function medicationGridMapperImpl(config: unknown) {
  const parsedConfig = medicationGridMapperConfigSchema.parse(config);
  
  // Pre-build a Map for O(1) lookups instead of repeated linear searches
  const medicationPrefixMap = new Map<string, { name: string; attributeId: number }>();
  
  Object.entries(parsedConfig.medicationMap).forEach(([medName, attributeId]) => {
    // Store with the underscore suffix for direct lookup
    medicationPrefixMap.set(medName + "_", { name: medName, attributeId });
  });

  return (response: string, _context: MappingContext): AttributeValueInput[] => {
    const attributes: AttributeValueInput[] = [];

    if (typeof response !== "string") {
      return [];
    }

    try {
      // Parse the JSON string and then validate its structure with Zod.
      const answers = medicationGridResponseSchema.parse(JSON.parse(response));

      Object.values(answers).forEach((valueStr) => {
        // Find which medication this answer is for by checking prefixes
        let medicationInfo: { name: string; attributeId: number } | undefined;
        
        // Check each prefix in the map
        for (const [prefix, info] of medicationPrefixMap) {
          if (valueStr.startsWith(prefix)) {
            medicationInfo = info;
            break;
          }
        }

        if (medicationInfo) {
          const statusType = valueStr.substring(medicationInfo.name.length + 1); // +1 for the '_'
          
          // Skip "not_taking" values - we can't infer past usage from this
          if (statusType === "not_taking") {
            return; // Use return instead of continue in forEach
          }
          
          const hasTaken = statusType !== "never";

          attributes.push({
            attributeDefinitionId: medicationInfo.attributeId,
            medicationAttributeValue: {
              hasTaken,
              intervals: hasTaken
                ? createMedicationInterval(statusType)
                : undefined,
            },
            isSkipped: false,
          });
        }
      });
    } catch (e) {
      console.error("Failed to parse medication grid response:", response, e);
      
      withScope((scope) => {
        scope.setTag("location", "medicationGridMapper");
        scope.setContext("mapperContext", {
          response,
          medicationMap: parsedConfig.medicationMap,
        });
        captureException(e);
      });
    }

    return attributes;
  };
}

/**
 * Creates a mapper for complex medication grid questions.
 * It parses JSON-stringified answers and maps them to medication attributes.
 * This function is memoized to avoid repeated config parsing.
 */
export const medicationGridMapper = memoizeMapperFactory(medicationGridMapperImpl); 